import { SkeletonBox } from "skullet/react";
import {
    ChevronSmallLeftIcon as ChevronLeftIcon,
    ChevronSmallRightIcon as ChevronRightIcon,
} from "@wags/ui/product-icons.tsx";

import { useDebouncer } from "@tanstack/react-pacer";
import { Button } from "@wags/ui/button.tsx";
import { ContentBottomSheet } from "@wags/ui/content-bottom-sheet.tsx";
import { Dialog, DialogContent, DialogHeaderRow } from "@wags/ui/dialog.tsx";
import { Input } from "@wags/ui/input.tsx";
import { cn } from "@wags/ui/ui-utils";
import { PROVIDER_LISTINGS_QUERY_MAX_LENGTH } from "@wags/catalog/listings/provider-listings-page.contract.ts";
import { type ReactNode, useState } from "react";
import type { ProviderDogListingCard } from "./provider-listing-card";
import { formatProviderListingsViewAllCaption } from "./provider-listings-count-copy";
import type { ProviderListingsPanelState } from "./provider-listings-query-flags";
import { useProviderListingsPagination } from "./providerListingsPagination";
import {
    PROVIDER_DIALOG_GRID_CLASS,
    PROVIDER_SHEET_GRID_CLASS,
} from "./providerListingsGrid";
import { GhostTileButton } from "@/components/shared/GhostTileButton";
import { PROVIDER_LISTINGS_PAGE_LIMIT } from "@/features/providers";
import {
    type CollectionWagsListing,
    ListingCardSkeletons,
} from "@/features/listings";

const DESKTOP_FILL_HIDE = [
    "",
    "",
    "",
    "",
    "",
    "hidden lg:block",
    "hidden lg:block",
    "hidden xl:block",
    "hidden xl:block",
];

const PANEL_GRID_CLASS =
    "grid min-h-0 content-start gap-4 overflow-y-auto overscroll-contain pb-4 scrollbar-none";

const PANEL_SKELETON_COUNT = 12;

function ListingSkeletonGrid({
    panelClassName,
    columnsClassName,
}: {
    panelClassName: string;
    columnsClassName: string;
}) {
    return (
        <div
            className={cn(
                "min-h-0 overflow-y-auto overscroll-contain scrollbar-none",
                panelClassName,
            )}
        >
            <ListingCardSkeletons
                count={PANEL_SKELETON_COUNT}
                className={cn("grid gap-4 pb-4", columnsClassName)}
            />
        </div>
    );
}

function ProviderDogGrid<TCard extends { id: string }>({
    items,
    renderItem,
    className,
    isStale,
}: {
    items: TCard[];
    renderItem: (item: TCard) => ReactNode;
    className: string;
    isStale: boolean;
}) {
    return (
        <div
            aria-busy={isStale}
            className={cn(
                PANEL_GRID_CLASS,
                "transition-opacity",
                isStale && "opacity-50",
                className,
            )}
        >
            {items.map((item) => (
                <div key={item.id}>{renderItem(item)}</div>
            ))}
        </div>
    );
}

type PaginationControlsProps = {
    currentPageIndex: number;
    pageRangeStart: number;
    pageRangeEnd: number;
    totalCount: number;
    hasNextPage: boolean;
    isBusy: boolean;
    hasPageError: boolean;
    panelState: ProviderListingsPanelState;
    onPrevious: () => Promise<void>;
    onNext: () => Promise<void>;
    onRetry: () => Promise<void>;
};

function PaginationCaption({
    panelState,
    hasPageError,
    isBusy,
    pageRangeStart,
    pageRangeEnd,
    totalCount,
    onRetry,
}: {
    panelState: ProviderListingsPanelState;
    hasPageError: boolean;
    isBusy: boolean;
    pageRangeStart: number;
    pageRangeEnd: number;
    totalCount: number;
    onRetry: () => Promise<void>;
}) {
    if (panelState === "failed") return null;

    if (hasPageError) {
        return (
            <Button
                type="button"
                variant="ghost"
                size="touch"
                className="text-doberman-black"
                disabled={isBusy}
                onClick={() => void onRetry()}
            >
                Couldn’t load. Try again
            </Button>
        );
    }

    if (panelState === "loading") {
        return (
            <>
                <div className="mx-auto w-24">
                    <SkeletonBox width="100%" height={10} radius={999} />
                </div>
                <output aria-live="polite" className="sr-only">
                    Loading dogs
                </output>
            </>
        );
    }

    if (panelState === "stale") {
        return <p className="text-label text-doberman-black">Searching…</p>;
    }

    return (
        <p className="text-label tabular-nums text-doberman-black">
            {pageRangeStart.toLocaleString()}–{pageRangeEnd.toLocaleString()} of{" "}
            {totalCount.toLocaleString()}
        </p>
    );
}

function PaginationControls({
    currentPageIndex,
    pageRangeStart,
    pageRangeEnd,
    totalCount,
    hasNextPage,
    isBusy,
    hasPageError,
    panelState,
    onPrevious,
    onNext,
    onRetry,
}: PaginationControlsProps) {
    return (
        <div className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-t border-doberman-black/10 bg-true-white pt-2">
            <Button
                type="button"
                variant="outline-ink"
                size="touch-narrow"
                disabled={currentPageIndex === 0 || isBusy}
                onClick={() => void onPrevious()}
            >
                <ChevronLeftIcon aria-hidden className="size-4" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sr-only sm:hidden">Previous page</span>
            </Button>
            <div className="min-w-0 text-center" aria-live="polite">
                <PaginationCaption
                    panelState={panelState}
                    hasPageError={hasPageError}
                    isBusy={isBusy}
                    pageRangeStart={pageRangeStart}
                    pageRangeEnd={pageRangeEnd}
                    totalCount={totalCount}
                    onRetry={onRetry}
                />
            </div>
            <Button
                type="button"
                variant="outline-ink"
                size="touch-narrow"
                disabled={!hasNextPage || isBusy || hasPageError}
                onClick={() => void onNext()}
            >
                <span className="hidden sm:inline">Next</span>
                <span className="sr-only sm:hidden">Next page</span>
                <ChevronRightIcon aria-hidden className="size-4" />
            </Button>
        </div>
    );
}

function formatOverflowCount(count: number): string {
    if (count < 1000) return `+${count}`;

    return `+${Math.floor(count / 1000)}k`;
}

function capProviderListingsQuery(value: string): string {
    return [...value].slice(0, PROVIDER_LISTINGS_QUERY_MAX_LENGTH).join("");
}

function ProviderListingsSearchInput({
    placeholder,
    value,
    onChange,
}: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <Input
            aria-label={placeholder}
            value={value}
            onChange={(event) =>
                onChange(capProviderListingsQuery(event.target.value))
            }
            placeholder={placeholder}
            type="search"

            maxLength={PROVIDER_LISTINGS_QUERY_MAX_LENGTH}
            variant="search"
        />
    );
}

function ViewAllPanelStateSlot({ children }: { children: ReactNode }) {
    return (
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            <div className="flex min-h-full flex-col items-center justify-center gap-4 px-6 py-6 text-center">
                {children}
            </div>
        </div>
    );
}

function NoSearchMatches({
    query,
    onClear,
}: {
    query: string;
    onClear: () => void;
}) {
    return (
        <ViewAllPanelStateSlot>
            <p className="text-body text-doberman-black/65">
                No listings match “{query}”.
            </p>
            <Button
                type="button"
                variant="outline"
                className="h-11"
                onClick={onClear}
            >
                Clear search
            </Button>
        </ViewAllPanelStateSlot>
    );
}

function PageLoadFailed({
    isRetrying,
    onRetry,
}: {
    isRetrying: boolean;
    onRetry: () => void;
}) {
    return (
        <ViewAllPanelStateSlot>
            <p className="text-section-title text-balance text-doberman-black">
                These dogs didn’t load
            </p>
            <p className="max-w-sm text-body text-pretty text-doberman-black/60">
                Try loading them again.
            </p>
            <Button
                type="button"
                variant="outline"
                className="h-11"
                disabled={isRetrying}
                onClick={onRetry}
            >
                Try again
            </Button>
        </ViewAllPanelStateSlot>
    );
}

function ViewAllPanelContent<TCard extends { id: string }>({
    activeQuery,
    onClearSearch,
    cards,
    gridKey,
    panelClassName,
    columnsClassName,
    renderCard,
    pagination,
}: {
    activeQuery: string;
    onClearSearch: () => void;
    cards: TCard[];
    gridKey: string;
    panelClassName: string;
    columnsClassName: string;
    renderCard: (card: TCard) => ReactNode;
    pagination: PaginationControlsProps;
}) {
    switch (pagination.panelState) {
        case "no-matches":
            return (
                <NoSearchMatches query={activeQuery} onClear={onClearSearch} />
            );
        case "failed":
            return (
                <PageLoadFailed
                    isRetrying={pagination.isBusy}
                    onRetry={() => void pagination.onRetry()}
                />
            );
        case "loading":
            return (
                <ListingSkeletonGrid
                    panelClassName={panelClassName}
                    columnsClassName={columnsClassName}
                />
            );
        default:
            return (
                <ProviderDogGrid
                    key={gridKey}
                    items={cards}
                    className={cn(panelClassName, columnsClassName)}
                    isStale={pagination.panelState === "stale"}
                    renderItem={renderCard}
                />
            );
    }
}

function ViewAllPanelBody<TCard extends { id: string }>({
    onSearchQueryChange,
    pagination,
    ...content
}: {
    onSearchQueryChange: (value: string) => void;
    activeQuery: string;
    cards: TCard[];
    gridKey: string;
    panelClassName: string;
    columnsClassName: string;
    renderCard: (card: TCard, index?: number) => ReactNode;
    pagination: PaginationControlsProps;
}) {
    return (
        <>
            <ViewAllPanelContent
                {...content}
                onClearSearch={() => onSearchQueryChange("")}
                pagination={pagination}
            />
            {pagination.panelState !== "no-matches" && (
                <PaginationControls {...pagination} />
            )}
        </>
    );
}

function ProviderListingsViewAllDialog({
    open,
    onOpenChange,
    title,
    caption,
    search,
    children,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: ReactNode;
    caption?: string;
    search: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="hidden md:block">
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    {...(caption ? {} : { "aria-describedby": undefined })}
                    showCloseButton={false}
                    className="flex h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden sm:max-w-7xl"
                >
                    <DialogHeaderRow title={title} description={caption}>
                        {search && (
                            <div className="w-full max-w-md">{search}</div>
                        )}
                    </DialogHeaderRow>
                    {children}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function ProviderListingsSection<TCard extends ProviderDogListingCard>({
    initialCards,
    providerId,
    totalCount,
    availableCount,
    fallbackImageUrl,
    mobileTitle,
    mobileDescription,
    viewAllTitle,
    searchPlaceholder = "Search by name or breed",
    emptyState,
    mapListingToCard,
    renderCard,
    desktopGridClassName = "grid-cols-3",
}: {
    initialCards: TCard[];
    providerId: string;
    totalCount: number;
    availableCount: number;
    fallbackImageUrl?: string;
    mobileTitle: ReactNode;
    mobileDescription?: ReactNode;
    viewAllTitle: ReactNode;
    searchPlaceholder?: string;
    emptyState: ReactNode;
    mapListingToCard: (
        listing: CollectionWagsListing,
        index: number,
        fallbackImageUrl: string | undefined,
    ) => TCard;
    renderCard: (
        card: TCard,
        index?: number,
        surface?: "mobile" | "desktop",
    ) => ReactNode;
    desktopGridClassName?: string;
}) {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeSearchQuery, setActiveSearchQuery] = useState("");
    const searchDebouncer = useDebouncer(setActiveSearchQuery, { wait: 220 });
    const handleSearchQueryChange = (value: string) => {
        setSearchQuery(value);
        const term = value.trim();
        if (term === "") {
            searchDebouncer.cancel();
            setActiveSearchQuery("");
            return;
        }

        searchDebouncer.maybeExecute(term);
    };
    const searchEnabled = totalCount > PROVIDER_LISTINGS_PAGE_LIMIT;
    const {
        featuredCardsMobile,
        featuredCardsDesktop,
        shouldShowMobileViewAll,
        shouldShowDesktopViewAll,
        currentPageCards,
        currentPageIndex,
        resolvedTotalCount,
        pageRangeStart,
        pageRangeEnd,
        hasNextPage,
        isQueryActive,
        isPageLoading,
        isSearching,
        panelState,
        hasPageError,
        goToPreviousPage,
        goToNextPage,
        retryFailedPage,
        isMobileSheetOpen,
        setIsMobileSheetOpen,
        isDesktopDialogOpen,
        setIsDesktopDialogOpen,
    } = useProviderListingsPagination({
        initialCards,
        providerId,
        totalCount,
        query: searchEnabled ? activeSearchQuery : undefined,
        fallbackImageUrl,
        mapListingToCard,
    });
    const searchInput = searchEnabled && (
        <ProviderListingsSearchInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchQueryChange}
        />
    );
    const panelBodyProps = {
        onSearchQueryChange: handleSearchQueryChange,
        activeQuery: activeSearchQuery,
        cards: currentPageCards,
        renderCard,
        pagination: {
            currentPageIndex,
            pageRangeStart,
            pageRangeEnd,
            totalCount: resolvedTotalCount,
            hasNextPage,
            isBusy: isPageLoading,
            hasPageError,
            panelState,
            onPrevious: goToPreviousPage,
            onNext: goToNextPage,
            onRetry: retryFailedPage,
        } satisfies PaginationControlsProps,
    };

    const handleViewAllOpenChange = (
        open: boolean,
        setOpen: (open: boolean) => void,
    ) => {
        handleSearchQueryChange("");
        setOpen(open);
    };
    return (
        <div>
            <h2 className="text-section-title text-doberman-black text-pretty md:hidden">
                {mobileTitle}
            </h2>
            {mobileDescription && (
                <div className="mt-2 md:hidden">{mobileDescription}</div>
            )}
            {initialCards.length > 0 ? (
                <>
                    <div className="mt-6 grid grid-cols-2 gap-4 md:hidden">
                        {featuredCardsMobile.map((dog, index) => (
                            <div key={dog.id}>
                                {renderCard(dog, index, "mobile")}
                            </div>
                        ))}
                        {shouldShowMobileViewAll && (
                            <div className="self-start">
                                <GhostTileButton
                                    headline={formatOverflowCount(
                                        totalCount - featuredCardsMobile.length,
                                    )}
                                    label="View all"
                                    onClick={() =>
                                        handleViewAllOpenChange(
                                            true,
                                            setIsMobileSheetOpen,
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={cn(
                            "mt-6 hidden gap-4 md:grid md:mt-0",
                            desktopGridClassName,
                        )}
                    >
                        {featuredCardsDesktop.map((dog, index) => (
                            <div
                                key={`${dog.id}-desktop`}
                                className={
                                    shouldShowDesktopViewAll
                                        ? DESKTOP_FILL_HIDE[index]
                                        : undefined
                                }
                            >
                                {renderCard(dog, index, "desktop")}
                            </div>
                        ))}
                        {shouldShowDesktopViewAll && (
                            <div className="self-start">
                                <GhostTileButton
                                    headline={
                                        <>
                                            <span className="lg:hidden">
                                                {formatOverflowCount(
                                                    totalCount - 5,
                                                )}
                                            </span>
                                            <span className="hidden lg:inline xl:hidden">
                                                {formatOverflowCount(
                                                    totalCount - 7,
                                                )}
                                            </span>
                                            <span className="hidden xl:inline">
                                                {formatOverflowCount(
                                                    totalCount - 9,
                                                )}
                                            </span>
                                        </>
                                    }
                                    label="View all"
                                    onClick={() =>
                                        handleViewAllOpenChange(
                                            true,
                                            setIsDesktopDialogOpen,
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                    {shouldShowMobileViewAll && (
                        <div className="md:hidden">
                            <ContentBottomSheet
                                title={viewAllTitle}
                                open={isMobileSheetOpen}
                                onOpenChange={(open) =>
                                    handleViewAllOpenChange(
                                        open,
                                        setIsMobileSheetOpen,
                                    )
                                }
                                bodyClassName="overflow-hidden"
                            >
                                <div className="flex h-[calc(85dvh_-_96px_-_env(safe-area-inset-bottom))] min-h-0 flex-col px-1 pt-4">
                                    {searchInput && (
                                        <div className="shrink-0">
                                            {searchInput}
                                        </div>
                                    )}
                                    <ViewAllPanelBody
                                        {...panelBodyProps}
                                        gridKey={`mobile-${activeSearchQuery}-page-${currentPageIndex}`}
                                        panelClassName="mt-4 min-h-0 flex-1"
                                        columnsClassName={
                                            PROVIDER_SHEET_GRID_CLASS
                                        }
                                    />
                                </div>
                            </ContentBottomSheet>
                        </div>
                    )}
                    {shouldShowDesktopViewAll && (
                        <ProviderListingsViewAllDialog
                            open={isDesktopDialogOpen}
                            onOpenChange={(open) =>
                                handleViewAllOpenChange(
                                    open,
                                    setIsDesktopDialogOpen,
                                )
                            }
                            title={viewAllTitle}
                            caption={
                                hasPageError
                                    ? undefined
                                    : formatProviderListingsViewAllCaption({
                                          isSearching,
                                          isQueryActive,
                                          matchCount: resolvedTotalCount,
                                          availableCount,
                                      })
                            }
                            search={searchInput}
                        >
                            <ViewAllPanelBody
                                {...panelBodyProps}
                                gridKey={`desktop-${activeSearchQuery}-page-${currentPageIndex}`}
                                panelClassName="mt-4 min-h-0 flex-1 border-t border-doberman-black/10 pt-4"
                                columnsClassName={PROVIDER_DIALOG_GRID_CLASS}
                            />
                        </ProviderListingsViewAllDialog>
                    )}
                </>
            ) : (
                <div className="mt-6 md:mt-0">{emptyState}</div>
            )}
        </div>
    );
}
